import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Clock, CheckCircle, XCircle, AlertCircle, Loader2,
  Search, Filter, ChevronRight, DollarSign
} from 'lucide-react';
import { Reservation } from '../types';
import { reservationApi } from '../services/api';
import Pagination from './Pagination';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { extractApiError } from '../utils/errorUtils';
import { formatDateRange, parseDateOnly } from '../utils/fairStatus'; // Issue #1672

type StatusFilter = 'all' | 'pending' | 'confirmed' | 'cancelled';
type TabFilter = 'all' | 'upcoming' | 'past';

export default function VendorReservationsList() {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [tabFilter, setTabFilter] = useState<TabFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadReservations = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const status = statusFilter === 'all' ? undefined : statusFilter;
        const response = await reservationApi.getMyReservations(status, currentPage);

        if (isMounted) {
          setReservations(response.items);
          setTotalPages(response.total_pages);
        }
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        if (isMounted) {
          setError(apiError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadReservations();

    return () => {
      isMounted = false;
    };
  }, [statusFilter, currentPage]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-primary-dark rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Confirmed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const filterReservations = (items: Reservation[]) => {
    let filtered = items;

    // Filter by tab (upcoming/past)
    const now = new Date();
    if (tabFilter === 'upcoming') {
      filtered = filtered.filter(r => {
        const end = parseDateOnly(r.fair_end_date);
        return end ? end >= now : true;
      });
    } else if (tabFilter === 'past') {
      filtered = filtered.filter(r => {
        const end = parseDateOnly(r.fair_end_date);
        return end ? end < now : false;
      });
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.fair_name.toLowerCase().includes(query) ||
        r.slot_number.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const filteredReservations = filterReservations(reservations);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Reservations</h1>
        <p className="text-gray-600 mt-1">Manage your slot reservations</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {(['all', 'upcoming', 'past'] as TabFilter[]).map(tab => (
          <button
            key={tab}
            onClick={() => setTabFilter(tab)}
            className={`px-4 py-2 font-medium transition-colors border-b-2 -mb-px ${
              tabFilter === tab
                ? 'border-primary text-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by fair name or slot..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as StatusFilter)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredReservations.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reservations found</h3>
          <p className="text-gray-600 mb-4">
            {statusFilter !== 'all' || searchQuery
              ? 'Try adjusting your filters'
              : 'Start by booking a slot at a fair'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-secondary hover:text-primary-dark font-medium"
          >
            Browse Fairs
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* Reservations List */}
      {!isLoading && filteredReservations.length > 0 && (
        <div className="space-y-4">
          {filteredReservations.map(reservation => {
            const fairEnd = parseDateOnly(reservation.fair_end_date);
            const isPastFair = fairEnd ? fairEnd < new Date() : false;
            const canReview = reservation.status === 'confirmed' && isPastFair;

            return (
              <div
                key={reservation.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                <Link
                  to={`/reservations/${reservation.id}`}
                  className="block"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">{reservation.fair_name}</h3>
                        {getStatusBadge(reservation.status)}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span>Slot #{reservation.slot_number}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {reservation.reservation_type === 'full_duration'
                            ? 'Full Duration'
                            : `${reservation.selected_dates?.length || 0} days`}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {formatCurrency(reservation.price_paid)} {/* Issue #905: Decimal.js formatting */}
                        </span>
                      </div>
                      {reservation.fair_start_date && reservation.fair_end_date && (
                        <p className="text-sm text-gray-500 mt-2">
                          {formatDateRange(reservation.fair_start_date, reservation.fair_end_date)}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Link>

                {/* Write Review Button (Issue #956) */}
                {canReview && reservation.vendor_id && reservation.fair_id && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <Link
                      to={`/vendors/${reservation.vendor_id}/review?fair_id=${reservation.fair_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                    >
                      Write a Review
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}
